import { CreateDataSourceRequest, DataSource } from "@coeff/api";
import { COLORS, FREE_DOMAINS } from "@coeff/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "antd";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import styled from "styled-components";

import { Typography } from "../../../common";
import { FlexRow } from "../../../common";
import { Button } from "../../../common/Button";
import { DisabledOverlay } from "../../../common/DisabledOverlay";
import { Graphics } from "../../../common/Graphics";
import { ConnectPlugIcon, Looker } from "../../../common/Icons";
import { Loader } from "../../../common/Loader";
import { SecurityInfo } from "../../../common/SecurityInfo";
import { useApiClient, useEventManagerContext, useMessage } from "../../../globals/contexts";
import { QueryCacheKey } from "../../context/AuthContext";
import { getBaseUrl, sentryCapture } from "../../utils";

import { AskDetailsStep } from "./AskDetailsStep";
import {
  Errors,
  HelperMessage,
  SecondaryMessage,
  LookerConnectionInfo,
  SupportAssistanceModal,
} from "./AuthorizeLookerContentV2";
import {
  CONNECTION_ERROR,
  DEFAULT_NOTICE,
  INVALID_CLIENT_ID_ERROR,
  LookerViews,
} from "./constants";
import { FormStep, validateLookerInputs } from "./FormStep";
import { OAuthConnections } from "./OAuthConnections";
import { OauthStep } from "./OauthStep";
import { RequestAccessStep } from "./RequestAccessStep";

const { Content, Footer } = Layout;

const StyledContent = styled(Content)<{ $hidePageHeader: boolean }>`
  && {
    height: 100%;
    min-height: ${props => (props.$hidePageHeader ? "auto" : "calc(100vh - 128px)")};
    margin-top: ${props => (props.$hidePageHeader ? "0" : "64px")};
    background-color: ${COLORS.white};
    padding: 14px 0px;

    .datasourceLogo {
      margin: auto;
      display: block;
    }

    .graphicsLogo {
      .datasourceLogo {
        width: 60px;
        height: 60px;
      }
    }
  }
`;

const StyledFooter = styled(Footer)`
  position: sticky;
  bottom: 0px;
  padding: 0px;
  background: ${COLORS.white};
`;

const ButtonSection = styled.section`
  width: 100%;
  display: flex;
  position: sticky;
  bottom: 0px;
  padding: 20px 5px 10px;
  background: ${COLORS.white};
  justify-content: center;

  .ant-btn {
    flex: 1;
    margin: 0px 10px;
    max-width: 200px;

    svg {
      width: 1rem;
      margin-right: 5px;
      position: relative;
      top: 2px;
    }
  }
`;

type Props = {
  isModal?: boolean;
  disableMessages?: boolean;
  dataSourceId: string | undefined;
  dataSource: DataSource | undefined;
  clientFlags: { is_gmail_like_domain?: boolean };
  event_props: {
    email?: string;
    domain?: string;
    first_name?: string;
    last_name?: string;
  };
  webappBaseUrl?: string;
  onDataSourceUpserted: (dataSource: DataSource) => void;
  onCancel: () => void;
  renderDataSourceItem?: (args: {
    dataSource?: DataSource;
    setAvailableSchemas: (schemas: string[]) => void;
    isEditConnectionId: boolean;
  }) => React.ReactNode;
};

export type AuthorizeLookerContentHandle = {
  goBack: () => void;
};

export const AuthorizeLookerContent = forwardRef<AuthorizeLookerContentHandle, Props>(
  (
    {
      isModal,
      disableMessages,
      dataSource: initialDataSource,
      dataSourceId: initialDataSourceId,
      clientFlags,
      event_props,
      webappBaseUrl = "",
      onDataSourceUpserted,
      onCancel,
      renderDataSourceItem,
    },
    ref
  ) => {
    const message = useMessage();

    const api = useApiClient();

    const { track } = useEventManagerContext();

    const queryClient = useQueryClient();

    const { data: dataSource } = useQuery(
      [QueryCacheKey.DATA_SOURCE, initialDataSource?.data_source_id],
      () =>
        api
          .getDataSource({
            data_source_id: initialDataSource?.data_source_id!,
          })
          .then(response => response.data.data_source),
      {
        enabled: Boolean(initialDataSource?.data_source_id),
        initialData: initialDataSource,
        onError: error => {
          console.error("Error fetching data source", error);

          sentryCapture({ error });
        },
      }
    );

    const [loading, setLoading] = useState<boolean>(false);

    const [error, setError] = useState<{
      title: string;
      message: string;
      secondaryMessage?: string;
      actionText?: string;
    }>();

    const [navigationStack, setNavigationStack] = useState<LookerViews[]>([]);
    const currentStep = navigationStack[navigationStack.length - 1];

    const [connectionInfo, setConnectionInfo] = useState<LookerConnectionInfo>({
      clientId: dataSource?.credentials.client_id || "",
      clientSecret: dataSource?.credentials.client_secret || "",
      url: dataSource?.credentials.url || "",
    });

    const [fieldsError, setFieldsError] =
      useState<Partial<Record<keyof LookerConnectionInfo, string>>>();

    const [showSupport, setShowSupport] = useState<boolean>();

    const dataSourceId = dataSource?.data_source_id || initialDataSourceId || "";

    const readOnly = dataSource?.user ? dataSource?.user.email !== event_props.email : false;

    const isLookerDisabled = FREE_DOMAINS.has(event_props.domain || "");

    const { data: apps = [], isLoading: isLoadingApps } = useQuery(
      [QueryCacheKey.LOOKER_APP_INFO],
      () => api.listLookerAppInfo(),
      {
        staleTime: 0,
        cacheTime: 0,
        retry: 2,
        select: response => response?.data?.apps_list,
        onSuccess: apps => {
          if (apps && apps.length > 0 && !dataSource) {
            navigateToStep(LookerViews.CONNECTION_LIST, { replace: true });
            return;
          }

          if (dataSource) {
            navigateToStep(LookerViews.FORM_STEP, { replace: true });
          } else {
            navigateToStep(LookerViews.ASK_DETAILS_STEP, { replace: true });
          }
        },
        onError: err => {
          message.error("Failed to load Looker apps");

          sentryCapture({
            error: err,
            name: "AuthorizeLookerV2: Fetch Looker Apps",
            screen: "AuthorizeLookerV2",
          });
        },
      }
    );

    const connectLooker = async () => {
      const isShared = dataSource?.is_shared_with_domain;

      const hasError = validateLookerInputs(connectionInfo);
      if (hasError) {
        setFieldsError(hasError);
        return;
      }

      setFieldsError(undefined);

      if (dataSourceId) {
        track("conn_edit_started", {
          import_data_source: "looker",
          conn_data_source: "looker",
          edited_sharing: isShared !== dataSource?.is_shared_with_domain,
          data_source_id: dataSourceId,
          looker_version: 2,
        });
      }

      setLoading(true);

      setError(undefined);

      try {
        const credentials = {
          client_id: connectionInfo.clientId,
          client_secret: connectionInfo.clientSecret,
          url: getBaseUrl(connectionInfo.url),
        };

        const checkResp = await api
          .lookerCheckConnection({
            looker_check_connection_request: {
              credentials,
            },
          })
          .then(resp => resp.data);

        if (checkResp.valid) {
          setConnectionInfo(pre => ({ ...pre, url: checkResp.base_url! }));

          const lookerConnectionMeta = await api
            .lookerGetDataSourceOwnerMetaData({
              looker_get_data_source_owner_meta_data_request: {
                credentials,
              },
            })
            .then(resp => resp.data);

          const isAdmin = lookerConnectionMeta.is_admin;
          const hasExistingOauth = lookerConnectionMeta.already_configured;

          if (
            isAdmin &&
            !hasExistingOauth &&
            !dataSourceId &&
            !isLookerDisabled &&
            !clientFlags.is_gmail_like_domain
          ) {
            navigateToStep(LookerViews.OAUTH_STEP);
            return;
          }

          const dataSourceBody: CreateDataSourceRequest["data_source"] = {
            ...dataSource,
            data_source_type: "looker",
            data_source_name: dataSource?.data_source_name || "Looker",
            credentials: { ...credentials, url: checkResp.base_url },
            is_shared_with_domain: isShared,
          };

          const { data: upsertDataSourceResponse } = await (dataSourceId
            ? api.updateDataSource({
                data_source_id: dataSourceId,
                update_data_source_request: {
                  data_source: dataSourceBody,
                },
              })
            : api.createDataSource({
                create_data_source_request: {
                  data_source: dataSourceBody,
                },
              }));

          track(dataSourceId ? "conn_edit_done" : "conn_create_done", {
            import_data_source: "looker",
            edited_sharing: dataSourceId
              ? isShared !== dataSource?.is_shared_with_domain
              : undefined,
            data_source_id: dataSourceId,
            is_looker_admin: !!isAdmin,
            looker_version: 2,
          });

          if (dataSourceId) {
            if (!disableMessages) {
              message.success("Connection saved");
            }
            queryClient.setQueryData([QueryCacheKey.DATA_SOURCE, dataSourceId], {
              ...upsertDataSourceResponse.data_source,
            });
          }

          onDataSourceUpserted(upsertDataSourceResponse.data_source);
        } else if (checkResp.source_error) {
          track(dataSourceId ? "conn_edit_failed" : "conn_create_failed", {
            import_data_source: "looker",
            data_source_id: dataSourceId,
            reason: checkResp.source_error,
            looker_version: 2,
          });

          setError({
            title: checkResp.title || INVALID_CLIENT_ID_ERROR.title,
            message: checkResp.source_error || INVALID_CLIENT_ID_ERROR.message,
          });
        } else {
          track(dataSourceId ? "conn_edit_failed" : "conn_create_failed", {
            import_data_source: "looker",
            data_source_id: dataSourceId,
            reason: checkResp.error,
            looker_version: 2,
          });
          sentryCapture({
            context: { response: checkResp },
            error: new Error("check_connection API returned valid=false but no structured error"),
            name: `AuthorizeLooker: ${
              dataSourceId ? "PUT" : "POST"
            } /api/data_source_apis/looker/check_connection`,
          });

          setError(INVALID_CLIENT_ID_ERROR);
        }
      } catch (error) {
        sentryCapture({
          error,
          name: `AuthorizeLooker: ${
            dataSourceId ? "PUT" : "POST"
          } /api/data_source_apis/looker/check_connection`,
        });
        setError(CONNECTION_ERROR);
      } finally {
        setLoading(false);
      }
    };

    const navigateToStep = (view: LookerViews, options: { replace?: boolean } = {}) => {
      setNavigationStack(prev => {
        if (options.replace) {
          return [...prev.slice(0, -1), view];
        }

        return [...prev, view];
      });
    };

    const goBack = () => {
      if (currentStep === LookerViews.FORM_STEP && showSupport === undefined && !dataSourceId) {
        setShowSupport(true);
        return;
      }

      if (navigationStack.length === 1) {
        onCancel();
        return;
      }

      setNavigationStack(prev => prev.slice(0, -1));
    };

    useImperativeHandle(ref, () => ({ goBack }));

    const renderFooter = () => {
      const isCTAHidden = [
        LookerViews.ASK_DETAILS_STEP,
        LookerViews.CONNECTION_LIST,
        LookerViews.OAUTH_STEP,
        LookerViews.REQUEST_ACCESS_STEP,
      ].includes(currentStep);

      if (currentStep === LookerViews.OAUTH_STEP && !dataSourceId) {
        return (
          <OauthStep
            connectionInfo={connectionInfo}
            event_props={event_props}
            onAuthorize={dataSource => {
              navigateToStep(LookerViews.FORM_STEP, { replace: true });

              onDataSourceUpserted(dataSource);
            }}
            onCancel={nextDataSource => {
              navigateToStep(LookerViews.FORM_STEP, { replace: true });

              onDataSourceUpserted(nextDataSource);

              track("conn_create_done", {
                import_data_source: "looker",
                data_source_id: nextDataSource.data_source_id,
                is_looker_admin: true,
                looker_version: 2,
              });
            }}
          />
        );
      }

      return (
        <StyledFooter>
          {!readOnly && !isCTAHidden && !isLoadingApps && (
            <ButtonSection>
              <Button type="primary" disabled={loading} onClick={() => connectLooker()}>
                <div>
                  {loading && <Loader color="white" />}
                  {!loading && (dataSourceId ? "Save" : "Connect")}
                </div>
              </Button>
            </ButtonSection>
          )}

          {!dataSourceId && !isModal && <SecurityInfo />}
        </StyledFooter>
      );
    };

    const showCurrentStep = (step?: LookerViews) => {
      switch (step) {
        case LookerViews.ASK_DETAILS_STEP:
          return (
            <AskDetailsStep
              onStepChange={values => {
                track("conn_create_started", {
                  import_data_source: "looker",
                  conn_data_source: "looker",
                  data_source_id: dataSourceId,
                  looker_version: 2,
                });
                if (
                  values.isAdmin ||
                  (values.isAdmin === false && values.hasCredentials === true)
                ) {
                  navigateToStep(LookerViews.FORM_STEP);
                } else {
                  navigateToStep(LookerViews.REQUEST_ACCESS_STEP);
                }
              }}
            />
          );

        case LookerViews.REQUEST_ACCESS_STEP:
          return <RequestAccessStep event_props={event_props} webappBaseUrl={webappBaseUrl} />;

        case LookerViews.OAUTH_STEP:
        case LookerViews.FORM_STEP:
          return (
            <>
              {!dataSourceId && (
                <SecondaryMessage {...DEFAULT_NOTICE} onNavigate={navigateToStep} />
              )}
              <Errors error={error} dataSource={dataSource} readOnly={readOnly} />
              <FormStep
                readOnly={readOnly || loading}
                connectionInfo={connectionInfo}
                onChanges={setConnectionInfo}
                error={fieldsError}
                dataSourceId={dataSourceId}
              />
              <HelperMessage />
              {step === LookerViews.OAUTH_STEP && <DisabledOverlay className="dark" />}
            </>
          );
        case LookerViews.CONNECTION_LIST:
          return (
            <>
              <OAuthConnections
                apps={apps}
                event_props={event_props}
                webappBaseUrl={webappBaseUrl}
                onAddNew={() => {
                  navigateToStep(LookerViews.ASK_DETAILS_STEP);
                }}
                onAuthorize={dataSource => {
                  onDataSourceUpserted(dataSource);
                }}
              />
            </>
          );

        default:
          return null;
      }
    };

    return (
      <>
        <StyledContent $hidePageHeader={isModal ?? false}>
          {!dataSourceId ? (
            <Graphics
              graphics={
                <div className="graphicsLogo">
                  <ConnectPlugIcon />
                  <Looker className="datasourceLogo" />
                </div>
              }
              title={
                <Typography fontWeight={700} fontSize={18} lineHeight={"24px"}>
                  {currentStep === LookerViews.REQUEST_ACCESS_STEP ? (
                    <>
                      Have your admin connect to <br /> Looker for you
                    </>
                  ) : (
                    "Let's Connect to Looker"
                  )}
                </Typography>
              }
            />
          ) : null}

          {renderDataSourceItem?.({
            dataSource,
            setAvailableSchemas: () => {},
            isEditConnectionId: Boolean(initialDataSourceId),
          })}

          {isLoadingApps && (
            <FlexRow style={{ padding: 24 }}>
              <Loader />
            </FlexRow>
          )}

          {showCurrentStep(currentStep)}
        </StyledContent>

        {renderFooter()}

        {showSupport && <SupportAssistanceModal onHideSupport={() => setShowSupport(false)} />}
      </>
    );
  }
);

AuthorizeLookerContent.displayName = "AuthorizeLookerContent";
